import produce from "immer";
import reducerUtils from "../../../../store/reducerUtils";
import ChatActions from "./chatActions";

const initialState = {
  data: {
    list: null,
    listCounter: 0,
    listFolderId: null,

    chat: null,
    messages: [],

    replyInProgress: false,
    replyPart: "",
    replyError: null,
    replyToolUses: null,
    replyThinkingBlocks: [], // Array of { blockIndex, content, startTime, isComplete }
    replyCurrentThinkingBlockIndex: null,
    replyAgenticTimeline: null, // Array of timeline entries: steps and plan updates
    replyAgenticSessionStatus: null
  },
};

export const chatSelectors = {
  getList: (state) => state.chats.data.list,
  getListCounter: (state) => state.chats.data.listCounter,
  getListFolderId: (state) => state.chats.data.listFolderId,
  getFolder: (state) => state.chats.data.folder,
  getChat: (state) => state.chats.data.chat,
  getMessages: (state) => state.chats.data.messages,
  getReplyInProgress: (state) => state.chats.data.replyInProgress,
  getReplyPart: (state) => state.chats.data.replyPart,
  getReplyError: (state) => state.chats.data.replyError,
  getReplyToolUses: (state) => state.chats.data.replyToolUses,
  getReplyThinkingBlocks: (state) => state.chats.data.replyThinkingBlocks,
  getReplyAgenticTimeline: (state) => state.chats.data.replyAgenticTimeline,
  getReplyAgenticSessionStatus: (state) => state.chats.data.replyAgenticSessionStatus,
};

const onLoadList = produce((draft, action) => {
  draft.data.list = action.list;
  draft.data.listFolderId = action.folderId;
});

const onListChanged = produce((draft) => {
  draft.data.listCounter++;
});

const onChatLoaded = produce((draft, action) => {
  const obj = action.chat;
  draft.data.chat = obj.chat;
  draft.data.messages = obj.messages;
  // Clear reply state when loading a new chat
  draft.data.replyInProgress = false;
  draft.data.replyError = null;
  draft.data.replyPart = "";
  draft.data.replyToolUses = null;
  draft.data.replyThinkingBlocks = [];
  draft.data.replyCurrentThinkingBlockIndex = null;
  draft.data.replyAgenticTimeline = null;
  draft.data.replyAgenticSessionStatus = null;
});

const onChatFolderLoaded = produce((draft, action) => {
  const obj = action.folder;
  draft.data.folder = obj.chat;
});

const onMessagePosted = produce((draft, action) => {
  const message = action.message;
  draft.data.messages.push({ role: "user", content: message });
  draft.data.replyInProgress = true;
  draft.data.replyError = null;
  draft.data.replyPart = "";
  draft.data.replyToolUses = null;
  draft.data.replyThinkingBlocks = [];
  draft.data.replyCurrentThinkingBlockIndex = null;
  draft.data.replyAgenticTimeline = null;
  draft.data.replyAgenticSessionStatus = null;
});

const onToolUsePosted = produce((draft, action) => {
  draft.data.replyInProgress = true;
  draft.data.replyError = null;
  draft.data.replyPart = "";
  draft.data.replyToolUses = null;
  draft.data.replyThinkingBlocks = [];
  draft.data.replyCurrentThinkingBlockIndex = null;
});

const onGotMessagePart = produce((draft, action) => {
  if (!draft.data.replyInProgress) {
    draft.data.replyInProgress = true;
    draft.data.replyError = null;
    draft.data.replyToolUses = null;
  }
  draft.data.replyPart = (draft.data.replyPart ?? "") + action.content;
  //console.log("onGotMessagePart ", draft.data.replyPart, action);
});

const onGotToolUses = produce((draft, action) => {
  draft.data.replyToolUses = action.toolUses;
});

const onGotThinkingContent = produce((draft, action) => {
  const {
    thinkingContent,
    thinkingBlockIndex,
    thinkingStartTimeMs,
    thinkingDurationMs,
  } = action;

  // If we have a duration without content, this is a completion signal for the current block
  if (
    thinkingDurationMs != null &&
    (!thinkingContent || thinkingContent === "") &&
    thinkingBlockIndex != null
  ) {
    const blockToComplete = draft.data.replyThinkingBlocks.find(
      (b) => b.blockIndex === thinkingBlockIndex
    );
    if (blockToComplete) {
      blockToComplete.isComplete = true;
      blockToComplete.durationMs = thinkingDurationMs;
    }
    return;
  }

  // If we have a block index and it's different from current, finalize the previous block
  if (
    thinkingBlockIndex != null &&
    draft.data.replyCurrentThinkingBlockIndex != null &&
    thinkingBlockIndex !== draft.data.replyCurrentThinkingBlockIndex
  ) {
    // Mark the previous block as complete (timing comes from backend)
    const prevBlock = draft.data.replyThinkingBlocks.find(
      (b) => b.blockIndex === draft.data.replyCurrentThinkingBlockIndex
    );
    if (prevBlock && thinkingDurationMs != null) {
      prevBlock.isComplete = true;
      prevBlock.durationMs = thinkingDurationMs;
    }
  }

  // Find or create the current thinking block
  if (thinkingBlockIndex != null) {
    let currentBlock = draft.data.replyThinkingBlocks.find(
      (b) => b.blockIndex === thinkingBlockIndex
    );

    if (!currentBlock) {
      // Create new block with backend timing
      currentBlock = {
        blockIndex: thinkingBlockIndex,
        content: thinkingContent || "",
        startTimeMs: thinkingStartTimeMs,
        durationMs: thinkingDurationMs,
        isComplete: false,
        isExpanded: false,
      };
      draft.data.replyThinkingBlocks.push(currentBlock);
    } else {
      // Append to existing block
      if (thinkingContent) {
        currentBlock.content += thinkingContent;
      }
      // Update timing if provided
      if (thinkingDurationMs != null) {
        currentBlock.durationMs = thinkingDurationMs;
      }
    }

    draft.data.replyCurrentThinkingBlockIndex = thinkingBlockIndex;
  }
});

const onFinishedMessageParts = produce((draft, action) => {
  draft.data.replyInProgress = false;
  draft.data.replyPart = "";
  draft.data.replyToolUses = null;

  // Mark the last thinking block as complete if any (duration should already be set by backend)
  if (draft.data.replyCurrentThinkingBlockIndex != null) {
    const lastBlock = draft.data.replyThinkingBlocks.find(
      (b) => b.blockIndex === draft.data.replyCurrentThinkingBlockIndex
    );
    if (lastBlock && !lastBlock.isComplete) {
      lastBlock.isComplete = true;
      // Duration already set by backend in thinkingDurationMs
    }
  }

  draft.data.replyThinkingBlocks = [];
  draft.data.replyCurrentThinkingBlockIndex = null;
  draft.data.messages = [
    ...(draft.data.messages ?? []).filter((item) => item.uid),
    ...action.response,
  ];
});

const onMessagePartError = produce((draft, action) => {
  draft.data.replyInProgress = false;
  draft.data.replyPart = "";
  draft.data.replyError = action.error;
  draft.data.replyToolUses = null;
  draft.data.replyThinkingBlocks = [];
  draft.data.replyCurrentThinkingBlockIndex = null;
  draft.data.replyAgenticTimeline = null;
  draft.data.replyAgenticSessionStatus = null;
});

const onGotAgentStep = produce((draft, action) => {
  const step = action.step;

  // Initialize timeline if needed
  if (!draft.data.replyAgenticTimeline) {
    draft.data.replyAgenticTimeline = [];
  }

  // Update session status
  draft.data.replyAgenticSessionStatus = step.sessionStatus;

  // Handle PLAN_UPDATE steps differently - extract plan tasks from stepResult
  if (step.stepType === 'PLAN_UPDATE') {
    let planTasks = null;
    if (step.stepResult) {
      try {
        planTasks = JSON.parse(step.stepResult);
      } catch (e) {
        console.error('Failed to parse plan tasks from PLAN_UPDATE step:', e);
      }
    }

    draft.data.replyAgenticTimeline.push({
      type: 'plan_update',
      timestamp: Date.now(),
      planTasks: planTasks,
      sessionStatus: step.sessionStatus,
    });
  } else {
    // Add regular step entry to timeline
    draft.data.replyAgenticTimeline.push({
      type: 'step',
      timestamp: Date.now(),
      stepType: step.stepType,
      stepStatus: step.stepStatus,
      stepNumber: step.stepNumber,
      title: step.title,
      content: step.content,
      stepResult: step.stepResult,
      taskId: step.taskId,
      taskText: step.taskText,
      errorMessage: step.errorMessage,
      sessionStatus: step.sessionStatus,
    });
  }
});

const onGotAgentFinished = produce((draft, action) => {
  const finishData = action.finishData;

  // Update session status
  draft.data.replyAgenticSessionStatus = finishData.sessionStatus;

  // Add finish entry (always, whether success or failure)
  if (draft.data.replyAgenticTimeline) {
    draft.data.replyAgenticTimeline.push({
      type: 'finish',
      timestamp: Date.now(),
      sessionStatus: finishData.sessionStatus,
      errorMessage: finishData.errorMessage,
    });
  }

  // Clear loading state - agent session is complete
  draft.data.replyInProgress = false;
  draft.data.replyPart = "";
  draft.data.replyToolUses = null;
  draft.data.replyThinkingBlocks = [];
  draft.data.replyCurrentThinkingBlockIndex = null;
  draft.data.replyAgenticTimeline = [];
  draft.data.replyAgenticSessionStatus = null;
});

const onGotMessageReply = produce((draft, action) => {
  const messages = action.reply.messages;
  draft.data.replyInProgress = false;
  draft.data.replyPart = "";
  draft.data.replyToolUses = null;
  draft.data.messages = [...draft.data.messages, ...messages];
});

const onMessageFileUploaded = produce((draft, action) => {
  const message = action.message;
  draft.data.messages = [...draft.data.messages, message];
});

const onMessageDeleted = produce((draft, action) => {
  const messageId = action.messageId;
  draft.data.messages = draft.data.messages.filter(
    (item) => item.uid !== messageId
  );
});

const onMessageUpdated = produce((draft, action) => {
  const { messageId, content } = action;
  draft.data.messages = draft.data.messages.map((item) => {
    if (item.uid === messageId) {
      return {
        ...item,
        content,
      };
    } else {
      return item;
    }
  });
});

const handlers = {
  [ChatActions.LIST_LOADED]: onLoadList,
  [ChatActions.LIST_CHANGED]: onListChanged,
  [ChatActions.LOADED]: onChatLoaded,
  [ChatActions.FOLDER_LOADED]: onChatFolderLoaded,
  [ChatActions.POSTED_MESSAGE]: onMessagePosted,
  [ChatActions.POSTED_TOOL_USE]: onToolUsePosted,
  [ChatActions.GOT_MESSAGE_PART]: onGotMessagePart,
  [ChatActions.GOT_TOOL_USES]: onGotToolUses,
  [ChatActions.GOT_THINKING_CONTENT]: onGotThinkingContent,
  [ChatActions.FINISHED_MESSAGE_PARTS]: onFinishedMessageParts,
  [ChatActions.MESSAGE_PART_ERROR]: onMessagePartError,
  [ChatActions.GOT_AGENT_STEP]: onGotAgentStep,
  [ChatActions.GOT_AGENT_FINISHED]: onGotAgentFinished,
  [ChatActions.GOT_MESSAGE_RELY]: onGotMessageReply,
  [ChatActions.MESSAGE_FILE_UPLOADED]: onMessageFileUploaded,
  [ChatActions.MESSAGE_DELETED]: onMessageDeleted,
  [ChatActions.MESSAGE_UPDATED]: onMessageUpdated,
};

const chatReducer = reducerUtils.createReducer(initialState, handlers);

export default chatReducer;
